import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/tailwind-utils';
import clsx from 'clsx';
import { Loader2, LucideIcon } from 'lucide-react';

// eslint-disable-next-line react-refresh/only-export-components
export const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-air-primary text-destructive-foreground hover:bg-air-primary-hover active:bg-air-primary-active',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        dismiss: 'border-gray-200 bg-gray-200 hover:bg-gray-200/80',
      },
      size: {
        default: 'h-10 px-4 py-2',
        xs: 'h-6 rounded-md px-1',
        sm: 'h-9 rounded-md px-3',
        md: 'h-10 rounded-md px-5 min-w-[88px]',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  icon?: LucideIcon;
  iconClassName?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, icon: Icon, iconClassName = '', loading, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const IconComp = loading ? (
      <Loader2 className={clsx('h-5 w-5 animate-spin', children && 'mr-2')} />
    ) : Icon ? (
      <Icon className={clsx('h-5 w-5', children && 'mr-2', iconClassName)} />
    ) : null;

    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} disabled={loading} {...props}>
        {IconComp}
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';
